import { CircleAlert, SearchX, ServerOff, TriangleAlert } from 'lucide-react';
import { EmptyState } from '@/shared/components/EmptyState';
import type { ServerLogScan, ServerLogSource } from '@/shared/types/api';

const MB = 1024 * 1024;

/** A scan that hit the byte budget or the time limit returned only part of the window. */
export function LogScanNotice({ scan }: { scan: ServerLogScan | null }) {
  if (!scan) return null;

  if (scan.stoppedReason === 'byteBudget' || scan.stoppedReason === 'timeout') {
    return (
      <div role="status" className="flex items-start gap-2 rounded-md border border-border bg-warning-subtle px-3 py-2 text-sm text-warning-foreground">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          Scanned {(scan.bytesScanned / MB).toFixed(1)} MB — results may be incomplete. Load older or narrow the time
          window.
        </span>
      </div>
    );
  }

  if (scan.stoppedReason === 'cursorExpired') {
    return (
      <div role="status" className="flex items-start gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>The older log file has rotated out since this page loaded. Refresh to start again.</span>
      </div>
    );
  }

  return null;
}

/** What to show instead of the table: the log source is missing, or nothing matched. */
export function LogEmptyState({ source, hasItems }: { source: ServerLogSource | null; hasItems: boolean }) {
  if (source && !source.available) {
    return (
      <div className="card">
        <EmptyState
          icon={ServerOff}
          title="Server logs unavailable"
          description={source.message ?? 'Log files are not available on this host.'}
        />
      </div>
    );
  }

  if (!hasItems) {
    return (
      <div className="card">
        <EmptyState
          icon={SearchX}
          title="No log entries match"
          description="Widen the time window, lower the minimum level or clear the search."
        />
      </div>
    );
  }

  return null;
}

/** The last load failed and there is nothing current to show: say so, rather than "no matches". */
export function LogErrorState({ message, loading, onRetry }: { message: string; loading: boolean; onRetry: () => void }) {
  return (
    <div className="card">
      <EmptyState
        icon={CircleAlert}
        title="Could not load server logs"
        description={message}
        action={
          <button type="button" className="btn-secondary" onClick={onRetry} disabled={loading}>
            Retry
          </button>
        }
      />
    </div>
  );
}
